import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Box, List, ListItem, Typography, Alert, Snackbar, ListItemButton, ListItemText, Avatar, Menu, MenuItem, Backdrop, CircularProgress, Dialog, DialogTitle, DialogContent, IconButton, TextField, DialogActions, DialogContentText, Button, Chip } from '@mui/material';
import { Add as AddIcon, Business as BusinessIcon, Warning as WarningIcon, ExpandMore as ExpandMoreIcon, Description as DescriptionIcon, Feedback as FeedbackIcon, Insights as InsightsIcon, Folder as FolderIcon, Assessment as AssessmentIcon, MoreVert as MoreVertIcon, WarningAmber as WarningAmberIcon } from '@mui/icons-material';
import DisclaimerModal from './components/DisclaimerModal';
import { LoginForm } from './components/LoginForm';
import { ProfileCompletionDialog } from './components/ProfileCompletionDialog';
import OrgMenu from './components/OrgMenu';
import UserMenu from './components/UserMenu';
import { WorkspaceManager } from './components/WorkspaceManager';
import { ResponseTimeline } from './components/ResponseTimeline';
import { MessageInput } from './components/MessageInput';
import { Insights } from './components/Insights';
import { Analysis } from './components/Analysis';
import { MetadataBrowser } from './components/MetadataBrowser';
import { OutputDocs } from './components/OutputDocs';
import { useApp } from './context/AppContext';
import { useAuth } from './context/AuthContext';
import { useWorkspaceManagement } from './hooks/useWorkspaceManagement';
import WorkspaceInitializationProgress from './components/WorkspaceInitializationProgress';
//import { getOrgNameFromInstanceUrl, clearAuthCookie } from './utils/cookies';
import { clearAuthCookie } from './utils/cookies';
import { apiService, Conversation, ApiResponse, ApiResponseItem, ApiMessageItem, Response } from './services/api';

// ThinkingMessage component for loading state
const ThinkingMessage: React.FC = () => {
  return (
    <ListItem sx={{ padding: 0, display: 'flex', justifyContent: 'flex-start', maxWidth: '100%', minWidth: 0 }}>
      <Box sx={{
        justifyContent: 'flex-start',
        width: '100%',
        backgroundColor: 'transparent',
        borderRadius: '0px',
        padding: '8px 0px',
        margin: '8px 0'
      }}>
        <Typography variant="body1" sx={{ 
          color: 'text.secondary', 
          fontStyle: 'italic',
          display: 'flex',
          alignItems: 'center',
          gap: 1
        }}>
          <Box
            component="span"
            sx={{
              width: 20,
              height: 20,
              border: '2px solid #e0e0e0',
              borderTop: '2px solid #1976d2',
              borderRadius: '50%',
              animation: 'spin 1s linear infinite',
              '@keyframes spin': {
                '0%': { transform: 'rotate(0deg)' },
                '100%': { transform: 'rotate(360deg)' }
              }
            }}
          />
          Thinking...
        </Typography>
      </Box>
    </ListItem>
  );
};

const AppV2: React.FC = () => {
	const { isAuthenticated, isLoading: isAuthLoading, user, logout } = useAuth();
  const { 
    clientAuth, 
    setClientAuth, 
    orgName, 
    setOrgName, 
    containerAppUrl,
    setContainerAppUrl,
		activeWorkspaceId,
		setActiveWorkspaceId,
    workspaces,
    loadWorkspaces,
    currentConversationId,
    setCurrentConversationId,
    currentResponseId,
    setCurrentResponseId,
    conversations,
    setConversations,
    addConversations,
    prependConversation,
    updateConversationInList,
    removeConversation,
    conversationsLoading,
    setConversationsLoading,
    conversationsHasMore,
    setConversationsHasMore,
    conversationsCursor,
    setConversationsCursor,
    showAllItems,
    setShowAllItems,
    currentConversationDetail,
    setCurrentConversationDetail,
    currentView,
    setCurrentView,
    selectedMetadataPath,
    setSelectedMetadataPath,
  } = useApp();
  
  const { 
    waitForWorkspaceReady, 
    refreshMetadata,
    progress, 
    hideProgress,
    isReady: isWorkspaceReady
  } = useWorkspaceManagement();
  
  const [inputValue, setInputValue] = useState('');
  const [alertOpen, setAlertOpen] = useState(false);
  const [alertMessage, setAlertMessage] = useState('');
  const [showBetaModal, setShowBetaModal] = useState(true);
  const [betaModalClosedOnce, setBetaModalClosedOnce] = useState(false);
  const [betaMenuAnchor, setBetaMenuAnchor] = useState<null | HTMLElement>(null);
  const [orgMenuAnchor, setOrgMenuAnchor] = useState<null | HTMLElement>(null);
	const [userMenuAnchor, setUserMenuAnchor] = useState<null | HTMLElement>(null);
  const [showWorkspaceManager, setShowWorkspaceManager] = useState(false);
  const [isWaitingForResponse, setIsWaitingForResponse] = useState(false);
  const [isLoadingConversation, setIsLoadingConversation] = useState(false);
  const [orgId, setOrgId] = useState<string | null>(null);
  const [insightsNavKey, setInsightsNavKey] = useState(0);
  const conversationListRef = useRef<HTMLDivElement>(null);
  const activeStreamCleanupRef = useRef<(() => void) | null>(null);
  const currentConversationIdRef = useRef<string | null>(null);

  // Conversation action menu state
	const [warningsDialogOpen, setWarningsDialogOpen] = useState(false);
	const handleWarningsDialogClose = () => setWarningsDialogOpen(false);

  const [convMenuAnchorEl, setConvMenuAnchorEl] = useState<null | HTMLElement>(null);
  const [convMenuId, setConvMenuId] = useState<string | null>(null);
  const [hoveredConvId, setHoveredConvId] = useState<string | null>(null);
  const [renamingConvId, setRenamingConvId] = useState<string | null>(null);
  const [renameValue, setRenameValue] = useState('');
  const [deleteConfirmConvId, setDeleteConfirmConvId] = useState<string | null>(null);
  const renameInputRef = useRef<HTMLInputElement>(null);

  // Focus the rename input after the menu finishes closing
  useEffect(() => {
    if (renamingConvId) {
      const timer = setTimeout(() => renameInputRef.current?.focus(), 50);
      return () => clearTimeout(timer);
    }
  }, [renamingConvId]);
  
  // Dynamic conversation list from context - now using conversations from API
  const conversationList = conversations;
  
  // Get org information from active workspace
  const activeWorkspace = activeWorkspaceId
    ? workspaces.find(workspace => workspace.workspace_id === activeWorkspaceId)
    : null;
  
  const orgInfo = {
    name: activeWorkspace?.connection.sf_instance_url || orgName || 'No Workspace',
    username: activeWorkspace?.connection.sf_username || clientAuth?.username || 'No User'
  };

	const userDisplayName = user?.first_name || user?.last_name
		? `${user?.first_name ?? ''} ${user?.last_name ?? ''}`.trim()
		: user?.display_name || user?.email || 'User';
	const userEmail = user?.email || 'Unknown email';

  // Helper function to refresh conversation detail
  // Only updates if the conversation matches the currently selected conversation
  // Also updates the conversation in the list (e.g., when topic is generated)
  const refreshConversationDetail = useCallback(async (conversationId: string) => {
    if (!activeWorkspaceId) return;
    // Only refresh if this is still the current conversation (user hasn't switched)
    // Check both state and ref to handle async state updates
    if (conversationId !== currentConversationId && conversationId !== currentConversationIdRef.current) {
      /* istanbul ignore next -- response.created always sets both state and ref to the same value; this guard fires only if both differ simultaneously, which cannot happen via normal SSE flow */
      return;
    }
    try {
			const convResponse = await apiService.getConversation(conversationId);
      if (convResponse.success && convResponse.data) {
        const conversationData = convResponse.data;
        // Double-check conversation is still current before updating (race condition protection)
        // Check both state and ref to handle async state updates
        if (conversationId === currentConversationId || conversationId === currentConversationIdRef.current) {
          setCurrentConversationDetail(conversationData);
          // Sync the sidebar label from the detail response. This acts as a reliable
          // fallback when the conversation.topic.updated SSE event is dropped (e.g. when
          // the browser delivers the final SSE frame together with the stream's done signal).
          if (conversationData.topic) {
            const conversationSummary: Conversation = {
              id: conversationData.id,
              object: 'conversation',
              org_id: conversationData.org_id,
              workspace_id: conversationData.workspace_id,
              snapshot_id: conversationData.snapshot_id,
              created_at: conversationData.created_at,
              updated_at: conversationData.updated_at,
              topic: conversationData.topic,
            };
            updateConversationInList(conversationSummary);
          }
        }
      }
    } catch (error) {
      console.error('Failed to reload conversation detail:', error);
    }
  }, [activeWorkspaceId, setCurrentConversationDetail, updateConversationInList, currentConversationId]);


  const handleSubmit = async () => {
    if (!orgId || !activeWorkspaceId) return;
    if (!inputValue.trim() || isWaitingForResponse || isLoadingConversation || !activeWorkspaceId || !containerAppUrl || !isWorkspaceReady) return;
    
    const userMessage = inputValue.trim();
    setInputValue(''); // Clear the input immediately
    setIsWaitingForResponse(true); // Set waiting state
    
    // Cancel any existing stream
    /* istanbul ignore next -- isWaitingForResponse guard on line 207 prevents re-entry while stream is active; ref is always cleared before isWaitingForResponse goes false */
    if (activeStreamCleanupRef.current) {
      activeStreamCleanupRef.current();
      activeStreamCleanupRef.current = null;
    }
    
    let streamResponseId: string | null = null;
    let streamConversationId: string | null = null;
    let responseInitialized = false;
    
    try {
      // Start streaming response
      const streamCleanup = await apiService.createResponse(
        userMessage,
        currentResponseId,
        true, // stream: true
        containerAppUrl,
        // onEvent handler
        (event) => {
          try {
            // Handle response.created event
            if (event.type === 'response.created') {
              streamResponseId = event.response_id;
              streamConversationId = event.conversation_id;
              
              // Update conversation state
              if (streamConversationId !== currentConversationId) {
                currentConversationIdRef.current = streamConversationId;
                setCurrentConversationId(streamConversationId);
              }
              setCurrentResponseId(streamResponseId);
              
              // Move conversation to top of list
              const conversation: Conversation = {
                id: streamConversationId,
                object: 'conversation',
                org_id: orgId,
                created_at: Math.floor(Date.now() / 1000),
                updated_at: Math.floor(Date.now() / 1000),
                topic: 'New Conversation'
              };
              prependConversation(conversation);
              
              // Initialize or update conversation detail with new response
              const now = Math.floor(Date.now() / 1000);
              
              // Create user message item from the input
              const userMessageItem: ApiMessageItem = {
                type: 'message',
                id: `msg_user_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
                role: 'user',
                content: userMessage, // userMessage is captured in the closure
                created_at: now,
              };
              
              const newResponse: Response = {
                id: streamResponseId!,
                object: 'response',
                org_id: orgId,
                conversation_id: streamConversationId!,
                previous_response_id: currentResponseId, // This is the previous response ID from state
                status: 'queued',
                created_at: now,
                completed_at: null,
                items: [userMessageItem], // Include the user message as the first item
                usage: null,
                error: null,
              };
              
              setTimeout(() => {
                setCurrentConversationDetail(prev => {
                  if (!prev || prev.id !== streamConversationId) {
                    // New conversation
                    return {
                      id: streamConversationId!,
                      object: 'conversation',
                      org_id: orgId,
                      created_at: now,
                      updated_at: now,
                      responses: [newResponse],
                    };
                  }
 
                  // Existing conversation - add new response
                  return {
                    ...prev,
                    updated_at: now,
                    responses: [...prev.responses, newResponse],
                  };
                });
              }, 0);
              
              responseInitialized = true;
              return;
            }
            
            // Handle conversation.topic.updated event
            if (event.type === 'conversation.topic.updated') {
              const topicEvent = event as import('./services/api').ConversationTopicUpdatedEvent;
              if (topicEvent.topic && topicEvent.conversation_id) {
                // Update conversation in the list with the new topic
                const conversationSummary: Conversation = {
                  id: topicEvent.conversation_id,
                  object: 'conversation',
                  org_id: orgId,
                  created_at: Math.floor(Date.now() / 1000), // Will be updated from detail if needed
                  updated_at: Math.floor(Date.now() / 1000),
                  topic: topicEvent.topic,
                };
                updateConversationInList(conversationSummary);
              }
              return;
            }
            
            // Process other events only after response is initialized
            if (!responseInitialized || !streamResponseId || !streamConversationId) {
              return;
            }
            
            // Process event to get item/status updates
            const processed = apiService.processStreamEvent(event);
            
            // Update conversation detail incrementally using latest state
            setTimeout(() => {
              setCurrentConversationDetail(prev => {
                if (!prev || prev.id !== streamConversationId) {
                  return prev;
                }

                const responseIndex = prev.responses.findIndex((r: Response) => r.id === streamResponseId);
                if (responseIndex === -1) {
                  return prev;
                }

                const updated = [...prev.responses];
                const currentResponse = { ...updated[responseIndex] };
                
                // Update status if provided
                if (processed.status) {
                  currentResponse.status = processed.status;
                  if (processed.status === 'completed' || processed.status === 'failed') {
                    currentResponse.completed_at = Math.floor(Date.now() / 1000);
                  }
                }
                
                // Update error if provided
                if (processed.error) {
                  currentResponse.error = processed.error;
                }
                
                // Add item if provided
                if (processed.item) {
                  if (!currentResponse.items) {
                    currentResponse.items = [];
                  }
                  // Check if item already exists (avoid duplicates)
                  const itemExists = currentResponse.items.some((item: ApiResponseItem) => 
                    item.id === processed.item!.id || 
                    (item.type === processed.item!.type && 
                     item.created_at === processed.item!.created_at)
                  );
                  if (!itemExists) {
                    currentResponse.items = [...currentResponse.items, processed.item];
                  }
                }
                
                updated[responseIndex] = currentResponse;
                return { ...prev, responses: updated };
              });
            }, 0);
          } catch (error) {
            console.error('Error processing stream event:', error);
          }
        },
        // onError handler
        (error) => {
          console.error('Stream error:', error);
          setIsWaitingForResponse(false);
          setAlertMessage(`Sorry, I encountered an error: ${error.message}`);
          setAlertOpen(true);
          activeStreamCleanupRef.current = null;
        },
        // onComplete handler
        () => {
          setIsWaitingForResponse(false);
          activeStreamCleanupRef.current = null;
          
          // Refresh conversation detail to get complete data with real IDs
          if (streamConversationId) {
            refreshConversationDetail(streamConversationId).catch(/* istanbul ignore next -- refreshConversationDetail has its own internal try-catch that swallows all errors; this .catch fires only if the function throws outside that block, which is unreachable */ err => {
              console.error('Failed to refresh conversation after stream:', err);
            });
          }
        }
      ) as (() => void) | ApiResponse<Response>;
      
      // Check if streaming was successful (returns cleanup function)
      if (typeof streamCleanup === 'function') {
        activeStreamCleanupRef.current = streamCleanup;
      } else {
        // Non-streaming response (shouldn't happen with stream=true, but handle gracefully)
        const apiResponse = streamCleanup as ApiResponse<Response>;
        if (apiResponse.success && apiResponse.data) {
          const response = apiResponse.data;
          setCurrentResponseId(response.id);
          if (response.conversation_id !== currentConversationId) {
            currentConversationIdRef.current = response.conversation_id;
            setCurrentConversationId(response.conversation_id);
          }
          setIsWaitingForResponse(false);
        } else {
          setIsWaitingForResponse(false);
          setAlertMessage(`Sorry, I encountered an error: ${apiResponse.error || 'Failed to create response'}`);
          setAlertOpen(true);
        }
      }
      
    } catch (error) {
      console.error('Conversation error:', error);
      setIsWaitingForResponse(false);
      const errorMsg = error instanceof Error ? error.message : 'Unknown error';
      setAlertMessage(`Sorry, I encountered an error: ${errorMsg}`);
      setAlertOpen(true);
      activeStreamCleanupRef.current = null;
    }
  };

  // Load conversations from API
	const loadConversations = useCallback(async (workspaceId?: string, reset: boolean = false) => {
		if (!workspaceId) return;
		
		setConversationsLoading(true);
		try {
			const cursor = reset ? undefined : conversationsCursor;
			const response = await apiService.getConversations(workspaceId, {
				limit: 20,
				order: 'desc',
				cursorTimestamp: cursor?.timestamp,
				cursorConversationId: cursor?.conversation_id,
			});
			
			if (response.success && response.data) {
				if (reset) {
					setConversations(response.data.data);
				} else {
					addConversations(response.data.data);
				}
				setConversationsHasMore(response.data.has_more);
				setConversationsCursor(response.data.next_cursor);
			} else {
				console.error('Failed to load conversations:', response.error);
			}
		} catch (error) {
			console.error('Error loading conversations:', error);
		} finally {
			setConversationsLoading(false);
		}
	}, [conversationsCursor, setConversationsLoading, setConversations, addConversations, setConversationsHasMore, setConversationsCursor]);

  // Load workspace
  const loadWorkspace = async (workspaceId: string) => {
    const result = await waitForWorkspaceReady(workspaceId);
    if (result.success && result.isReady) {
      hideProgress();
      await loadWorkspaces();
      if (result.orgId) {
        setOrgId(result.orgId);
				setActiveWorkspaceId(result.workspaceId ?? workspaceId);
        if (result.containerAppUrl) {
          setContainerAppUrl(result.containerAppUrl);
        }
			await loadConversations(result.workspaceId ?? workspaceId, true);
      }
    } else {
      const errorMessage = result.error || 'Workspace is not ready';
      setAlertMessage(`Workspace preparation: ${errorMessage}`);
      setAlertOpen(true);
    }
  };

  const handleCloseAlert = () => {
    setAlertOpen(false);
  };

  const clearConversation = () => {
    currentConversationIdRef.current = null;
    setCurrentConversationId(null);
    setCurrentResponseId(null);
    setCurrentConversationDetail(null);
  };


  const initialize = async () => {
    // Load workspaces first and get the result directly
    const loadedWorkspaces = await loadWorkspaces();

    if (activeWorkspaceId) {
      clearConversation();
      await loadWorkspace(activeWorkspaceId);
    } else if (loadedWorkspaces.length > 0) {
      const firstWorkspaceId = loadedWorkspaces[0].workspace_id;
      setActiveWorkspaceId(firstWorkspaceId);
      clearConversation();
      // Update last selected timestamp when auto-selecting workspace
      try {
        await apiService.updateWorkspaceLastSelected(firstWorkspaceId);
      } catch (error) {
        console.error('Failed to update last selected timestamp:', error);
      }
      await loadWorkspace(firstWorkspaceId);
    } else {
      // No workspaces - show WorkspaceManager
      setOrgName(null);
      clearConversation();
      setShowWorkspaceManager(true);
    }
  };

  const handleCloseBetaModal = () => {
    setShowBetaModal(false);
    
    // Initialize workspace (demo or from cookie) on first close of beta modal
    if (!betaModalClosedOnce) {
      setBetaModalClosedOnce(true);
      initialize();
    }
  };

  const handleBetaMenuClick = (event: React.MouseEvent<HTMLElement>) => {
    setBetaMenuAnchor(event.currentTarget);
  };

  const handleBetaMenuClose = () => {
    setBetaMenuAnchor(null);
  };

  const handleBetaDetails = () => {
    setShowBetaModal(true);
    handleBetaMenuClose();
  };

  const handleReleaseNotes = () => {
    window.open('release_notes.html', '_blank');
    handleBetaMenuClose();
  };

  const handleLeaveFeedback = () => {
    window.location.href = 'mailto:aclark_rnd@fusionrm.com';
    handleBetaMenuClose();
  };

  const handleNewConversation = () => {
    // Clear current conversation
    currentConversationIdRef.current = null;
    setCurrentConversationId(null);
    setCurrentResponseId(null);
    setCurrentConversationDetail(null);
    setCurrentView('conversation');
  };

  const handleAnalysisClick = () => {
    setCurrentView('analysis');
    currentConversationIdRef.current = null;
    setCurrentConversationId(null);
    setCurrentResponseId(null);
    setCurrentConversationDetail(null);
  };

  const handleInsightsClick = () => {
    setCurrentView('insights');
    setInsightsNavKey(k => k + 1);
    // Clear conversation selection when switching to insights
    currentConversationIdRef.current = null;
    setCurrentConversationId(null);
    setCurrentResponseId(null);
    setCurrentConversationDetail(null);
  };

  const handleMetadataClick = () => {
    setCurrentView('metadata');
    // Clear conversation selection when switching to metadata
    currentConversationIdRef.current = null;
    setCurrentConversationId(null);
    setCurrentResponseId(null);
    setCurrentConversationDetail(null);
  };

  const handleDocumentsClick = () => {
    setCurrentView('documents');
    currentConversationIdRef.current = null;
    setCurrentConversationId(null);
    setCurrentResponseId(null);
    setCurrentConversationDetail(null);
  };

  // Load conversation when selected
  const handleConversationSelect = async (conversationId: string) => {
    // Cancel any existing stream when switching conversations
    if (activeStreamCleanupRef.current) {
      activeStreamCleanupRef.current();
      activeStreamCleanupRef.current = null;
    }
    
    // Clear waiting state
    setIsWaitingForResponse(false);
    
    // Set loading state
    setIsLoadingConversation(true);
    
    currentConversationIdRef.current = conversationId;
    setCurrentConversationId(conversationId);
    setCurrentView('conversation');
    
    if (!orgId && !activeWorkspaceId) {
      setIsLoadingConversation(false);
      setAlertMessage('Workspace not initialized');
      setAlertOpen(true);
      return;
    }
    
    try {
			const response = await apiService.getConversation(conversationId);
      if (response.success && response.data) {
        // Store the full conversation detail for timeline view
        setCurrentConversationDetail(response.data);
        
        // Set currentResponseId to the latest response in the conversation
        // so new messages get added via previous_response_id instead of creating a new conversation
        if (response.data.responses && response.data.responses.length > 0) {
          // Use the last response in the array (assumed to be the latest)
          const latestResponse = response.data.responses[response.data.responses.length - 1];
          setCurrentResponseId(latestResponse.id);
          
          // If the latest response is still in progress, fetch once to show current state
          // (no polling - streaming would have been active if we were the one who started it)
          if (latestResponse.status === 'queued' || latestResponse.status === 'in_progress') {
            // Just show the current state - no background polling
            // The response will complete in the background and user can refresh if needed
          }
        } else {
          // No responses yet, clear currentResponseId
          setCurrentResponseId(null);
        }
      } else {
        setAlertMessage(response.error || 'Failed to load conversation');
        setAlertOpen(true);
      }
    } catch (error) {
      console.error('Error loading conversation:', error);
      setAlertMessage('Failed to load conversation');
      setAlertOpen(true);
    } finally {
      setIsLoadingConversation(false);
    }
  };

  const handleConvMenuOpen = (event: React.MouseEvent<HTMLElement>, convId: string) => {
    event.stopPropagation();
    setConvMenuAnchorEl(event.currentTarget);
    setConvMenuId(convId);
  };

  const handleConvMenuClose = () => {
    setConvMenuAnchorEl(null);
    setConvMenuId(null);
  };

  const handleRenameOpen = () => {
    const conv = conversations.find(c => c.id === convMenuId);
    setRenameValue(conv?.topic || '');
    setRenamingConvId(convMenuId);
    handleConvMenuClose();
  };

  const handleRenameCommit = async (convId: string) => {
    const trimmed = renameValue.trim();
    if (!trimmed) {
      setRenamingConvId(null);
      return;
    }
    const result = await apiService.renameConversation(convId, trimmed);
    if (result.success && result.data) {
      updateConversationInList(result.data);
      if (currentConversationId === convId && currentConversationDetail) {
        setCurrentConversationDetail(prev => prev ? { ...prev, topic: trimmed } : prev);
      }
    } else {
      setAlertMessage(result.error || 'Failed to rename conversation');
      setAlertOpen(true);
    }
    setRenamingConvId(null);
  };

  const handleArchive = async () => {
    const convId = convMenuId;
    handleConvMenuClose();
    if (!convId) return;
    const result = await apiService.archiveConversation(convId);
    if (result.success) {
      removeConversation(convId);
      if (currentConversationId === convId) {
        setCurrentConversationId(null);
        setCurrentConversationDetail(null);
      }
    } else {
      setAlertMessage(result.error || 'Failed to archive conversation');
      setAlertOpen(true);
    }
  };

  const handleDeleteOpen = () => {
    setDeleteConfirmConvId(convMenuId);
    handleConvMenuClose();
  };

  const handleDeleteConfirm = async () => {
    const convId = deleteConfirmConvId;
    setDeleteConfirmConvId(null);
    if (!convId) return;
    const result = await apiService.deleteConversation(convId);
    if (result.success) {
      removeConversation(convId);
      if (currentConversationId === convId) {
        setCurrentConversationId(null);
        setCurrentConversationDetail(null);
      }
    } else {
      setAlertMessage(result.error || 'Failed to delete conversation');
      setAlertOpen(true);
    }
  };

  const handleOrgClick = (event: React.MouseEvent<HTMLElement>) => {
    setOrgMenuAnchor(event.currentTarget);
  };

  const handleOrgMenuClose = () => {
    setOrgMenuAnchor(null);
  };

	const handleUserMenuClick = (event: React.MouseEvent<HTMLElement>) => {
		setUserMenuAnchor(event.currentTarget);
	};

	const handleUserMenuClose = () => {
		setUserMenuAnchor(null);
	};

  const handleSwitchOrg = () => {
    setShowWorkspaceManager(true);
  };

  const handleWorkspaceManagerClose = () => {
    // Only allow closing if there's at least one workspace
    if (workspaces.length > 0) {
      setShowWorkspaceManager(false);
    }
  };

  const handleWorkspaceSelect = async (workspaceId: string) => {
    setActiveWorkspaceId(workspaceId);
    clearConversation();
    
		// Update last selected timestamp when workspace is selected
		try {
			await apiService.updateWorkspaceLastSelected(workspaceId);
		} catch (error) {
			// Log but don't block - this is a best-effort update
			console.error('Failed to update last selected timestamp:', error);
		}
    
    setShowWorkspaceManager(false);
    await loadWorkspace(workspaceId);
  };

  const handleWorkspaceAdded = async () => {
    await loadWorkspaces();
  };

  const handleLogout = () => {
    clearAuthCookie();
    setClientAuth(null);
    setOrgName(null);
		setActiveWorkspaceId(null);
    setOrgId(null);
    setContainerAppUrl(null);
    clearConversation();
    // Show WorkspaceManager if no workspaces, otherwise just clear active workspace
    if (workspaces.length === 0) {
      setShowWorkspaceManager(true);
    }
  };

	const handleUserLogout = async () => {
		try {
			await logout();
		} catch (error) {
			console.error('Failed to logout:', error);
		} finally {
			handleLogout();
		}
	};

  const handleRefreshMetadata = async () => {
    if (!activeWorkspaceId) {
      setAlertMessage('Workspace not initialized');
      setAlertOpen(true);
      return;
    }

		const result = await refreshMetadata(
			activeWorkspaceId || undefined,
			activeWorkspace?.connection.connection_id || undefined
		);
    
    if (result.success) {
      await loadWorkspaces();
    } else {
      setAlertMessage(`Metadata refresh failed: ${result.error || 'Unknown error'}`);
      setAlertOpen(true);
    }
  };


  // Cleanup stream on unmount
  useEffect(() => {
    return () => {
      if (activeStreamCleanupRef.current) {
        activeStreamCleanupRef.current();
        activeStreamCleanupRef.current = null;
      }
    };
  }, []);

  // Infinite scroll handler for conversation list
  useEffect(() => {
    const handleScroll = () => {
      if (!conversationListRef.current || !activeWorkspaceId || conversationsLoading || !conversationsHasMore) {
        return;
      }

      const element = conversationListRef.current;
      const scrollTop = element.scrollTop;
      const scrollHeight = element.scrollHeight;
      const clientHeight = element.clientHeight;

      // Load more when within 100px of bottom
      if (scrollHeight - scrollTop - clientHeight < 100) {
				loadConversations(activeWorkspaceId || undefined, false);
      }
    };

    const listElement = conversationListRef.current;
    if (listElement) {
      listElement.addEventListener('scroll', handleScroll);
      return () => {
        listElement.removeEventListener('scroll', handleScroll);
      };
    }
  }, [activeWorkspaceId, conversationsLoading, conversationsHasMore, loadConversations]);

  const handleCopyMessage = (content: string) => {
    navigator.clipboard.writeText(content).then(() => {
      setAlertMessage('Message copied to clipboard!');
      setAlertOpen(true);
    }).catch(() => {
      setAlertMessage('Failed to copy message');
      setAlertOpen(true);
    });
  };

  const handleGoodResponse = () => {
    setAlertMessage('Thank you for the positive feedback!');
    setAlertOpen(true);
  };

  const handleBadResponse = () => {
    setAlertMessage('Thank you for the feedback. We\'ll work to improve!');
    setAlertOpen(true);
  };

	const shouldPromptName = Boolean(user) && (!user?.first_name?.trim() || !user?.last_name?.trim());

	if (isAuthLoading) {
		return (
			<Box display="flex" alignItems="center" justifyContent="center" minHeight="400px">
				<CircularProgress />
			</Box>
		);
	}

	if (!isAuthenticated) {
		return <LoginForm />;
	}

	return (
		<>
			<ProfileCompletionDialog open={shouldPromptName} />
			<Box sx={{ height: '100vh', display: 'flex', backgroundColor: '#ffffff' }}>
      <style>{`
        .markdown p {
          margin-top: 0.5rem;
          margin-bottom: 0.5rem;
        }
      `}</style>
      {/* Left Navigation Panel */}
      <Box sx={{ 
        width: 300, 
        backgroundColor: '#f5f5f5', 
        borderRight: '1px solid #e0e0e0',
        display: 'flex',
        flexDirection: 'column'
      }}>
				{/* Org Information - Fixed at top */}
				<Box sx={{ p: 1, borderBottom: '1px solid #e0e0e0' }}>
          <ListItem disablePadding>
            <ListItemButton onClick={handleOrgClick} dense>
							<Avatar sx={{ mr: 2, backgroundColor: '#1976d2' }}>
								<BusinessIcon />
							</Avatar>
              <ListItemText 
                primary={orgInfo.name}
                secondary={orgInfo.username}
                slotProps={{ primary: { fontWeight: 'bold' } }}
              />
            </ListItemButton>
          </ListItem>
					{activeWorkspace?.snapshot_apply_warnings && (() => {
						let count = 0;
						try { count = JSON.parse(activeWorkspace.snapshot_apply_warnings).skipped_count; } catch { /* ignore */ }
						return count > 0 ? (
							<Box sx={{ px: 1, pb: 0.5 }}>
								<Chip
									icon={<WarningAmberIcon />}
									label={`${count} metadata file${count !== 1 ? 's' : ''} skipped`}
									color="warning"
									size="small"
									onClick={() => setWarningsDialogOpen(true)}
									clickable
									sx={{ width: '100%', justifyContent: 'flex-start' }}
								/>
							</Box>
						) : null;
					})()}
				</Box>

         {/* Scrollable Conversation Section */}
         <Box 
           ref={conversationListRef}
           sx={{ flex: 1, overflow: 'auto', p: 1 }}
         >
           {/* New Conversation Button */}
           <ListItem disablePadding>
             <ListItemButton
               onClick={handleNewConversation}
               dense
             >
               <AddIcon sx={{ mr: 2, fontSize: '1.2rem' }} />
               <ListItemText 
                 primary="New Conversation"
                 slotProps={{ 
                   primary: { 
                     fontSize: '0.9rem',
                     fontWeight: 'normal'
                   }
                 }}
               />
             </ListItemButton>
           </ListItem>

           {/* Org Details Section */}
           <Typography variant="subtitle2" sx={{ px: 2, py: 1, color: 'text.secondary', fontWeight: 'bold' }}>
             Org Details
           </Typography>
           <List sx={{ p: 0 }}>
             <ListItem disablePadding>
               <ListItemButton
                 selected={currentView === 'analysis'}
                 onClick={handleAnalysisClick}
                 dense
               >
                 <AssessmentIcon sx={{ mr: 2, fontSize: '1.2rem' }} />
                 <ListItemText 
                   primary="Analysis"
                   slotProps={{ 
                     primary: { 
                       fontSize: '0.9rem',
                       fontWeight: currentView === 'analysis' ? 'bold' : 'normal'
                     }
                   }}
                 />
               </ListItemButton>
             </ListItem>
             <ListItem disablePadding>
               <ListItemButton
                 selected={currentView === 'insights'}
                 onClick={handleInsightsClick}
                 dense
               >
                 <InsightsIcon sx={{ mr: 2, fontSize: '1.2rem' }} />
                 <ListItemText 
                   primary="Insights"
                   slotProps={{ 
                     primary: { 
                       fontSize: '0.9rem',
                       fontWeight: currentView === 'insights' ? 'bold' : 'normal'
                     }
                   }}
                 />
               </ListItemButton>
             </ListItem>
             <ListItem disablePadding>
               <ListItemButton
                 selected={currentView === 'metadata'}
                 onClick={handleMetadataClick}
                 dense
               >
                 <FolderIcon sx={{ mr: 2, fontSize: '1.2rem' }} />
                 <ListItemText 
                   primary="Workspace Files"
                   slotProps={{ 
                     primary: { 
                       fontSize: '0.9rem',
                       fontWeight: currentView === 'metadata' ? 'bold' : 'normal'
                     }
                   }}
                 />
               </ListItemButton>
             </ListItem>
             <ListItem disablePadding>
               <ListItemButton
                 selected={currentView === 'documents'}
                 onClick={handleDocumentsClick}
                 dense
               >
                 <DescriptionIcon sx={{ mr: 2, fontSize: '1.2rem' }} />
                 <ListItemText 
                   primary="Documents"
                   slotProps={{ 
                     primary: { 
                       fontSize: '0.9rem',
                       fontWeight: currentView === 'documents' ? 'bold' : 'normal'
                     }
                   }}
                 />
               </ListItemButton>
             </ListItem>
           </List>

           {/* Conversation List */}
           <Typography variant="subtitle2" sx={{ px: 2, py: 1, color: 'text.secondary', fontWeight: 'bold' }}>
             Conversations
           </Typography>
           <List sx={{ p: 0 }}>
             {conversationList.length === 0 && !conversationsLoading ? (
               <ListItem>
                 <ListItemText 
                   primary="No conversations yet"
                   slotProps={{ 
                     primary: { 
                       fontSize: '0.9rem',
                       color: 'text.secondary'
                     }
                   }}
                 />
               </ListItem>
             ) : (
               conversationList.map((conv) => {
                 // Use topic if available, otherwise fall back to conversation ID substring
                 const preview = conv.topic || `Conversation ${conv.id.slice(-8)}`;
                 const isRenaming = renamingConvId === conv.id;
                 const showMenuBtn = hoveredConvId === conv.id || convMenuId === conv.id;
                 return (
                  <ListItem
                    key={conv.id}
                    disablePadding
                    onMouseEnter={() => setHoveredConvId(conv.id)}
                    onMouseLeave={() => setHoveredConvId(null)}
                  >
                    {isRenaming ? (
                      <Box sx={{ px: 1, py: 0.5, width: '100%' }}>
                        <TextField
                          inputRef={renameInputRef}
                          fullWidth
                          size="small"
                          value={renameValue}
                          onChange={(e) => setRenameValue(e.target.value)}
                          onBlur={() => handleRenameCommit(conv.id)}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') handleRenameCommit(conv.id);
                            if (e.key === 'Escape') setRenamingConvId(null);
                          }}
                          inputProps={{ style: { fontSize: '0.9rem' } }}
                        />
                      </Box>
                    ) : (
                      <ListItemButton
                        selected={currentConversationId === conv.id}
                        onClick={() => handleConversationSelect(conv.id)}
                        dense
                        sx={{ pr: 0.5, display: 'flex', alignItems: 'center' }}
                      >
                        <ListItemText
                          primary={preview}
                          title={preview}
                          slotProps={{
                            primary: {
                              fontSize: '0.9rem',
                              fontWeight: currentConversationId === conv.id ? 'bold' : 'normal',
                              sx: {
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                              },
                            },
                          }}
                        />
                        <IconButton
                          size="small"
                          onClick={(e) => handleConvMenuOpen(e, conv.id)}
                          sx={{
                            opacity: showMenuBtn ? 1 : 0,
                            transition: 'opacity 0.15s',
                            flexShrink: 0,
                            p: '2px',
                            ml: 0.5,
                          }}
                        >
                          <MoreVertIcon fontSize="small" />
                        </IconButton>
                      </ListItemButton>
                    )}
                  </ListItem>
                 );
               })
             )}
             {conversationsLoading && (
               <ListItem>
                 <ListItemText 
                   primary="Loading..."
                   slotProps={{ 
                     primary: { 
                       fontSize: '0.9rem',
                       color: 'text.secondary'
                     }
                   }}
                 />
               </ListItem>
             )}
           </List>
         </Box>

         {/* User + Beta Menu - Fixed at bottom */}
         <Box sx={{ borderTop: '1px solid #e0e0e0', p: 1 }}>
           <ListItem disablePadding>
             <ListItemButton onClick={handleUserMenuClick} dense>
               <Avatar sx={{ mr: 2, backgroundColor: '#616161' }}>
                 {(userDisplayName || 'U').charAt(0).toUpperCase()}
               </Avatar>
               <ListItemText
                 primary={userDisplayName}
                 secondary={userEmail}
                 slotProps={{ primary: { fontWeight: 'bold' } }}
               />
             </ListItemButton>
           </ListItem>
           <ListItem disablePadding sx={{ mt: 1 }}>
             <ListItemButton
               onClick={handleBetaMenuClick}
               dense
               sx={{ 
                 backgroundColor: 'warning.light',
                 color: 'warning.contrastText',
                 '&:hover': {
                   backgroundColor: 'warning.main',
                 }
               }}
             >
               <WarningIcon sx={{ mr: 2, fontSize: '1.2rem' }} />
               <ListItemText 
                 primary="PoC Info"
                 slotProps={{ 
                   primary: { 
                     fontSize: '0.9rem',
                     fontWeight: 'bold'
                   }
                 }}
               />
               <ExpandMoreIcon sx={{ fontSize: '1rem' }} />
             </ListItemButton>
           </ListItem>
         </Box>
      </Box>

      {/* Main Content Area */}
      <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', minWidth: 0, minHeight: 0, overflow: 'hidden' }}>
        {/* Render content based on current view */}
        {currentView === 'conversation' && (
          <>
            {/* Outer container with full width and scroll */}
            <Box sx={{ flex: 1, overflow: 'auto', display: 'flex', justifyContent: 'center', minWidth: 0 }}>
              {/* Inner container with max width for conversation content */}
              <Box sx={{ width: '100%', maxWidth: 800, p: 2, display: 'flex', flexDirection: 'column', minWidth: 0 }}>
                {/* Response Timeline - show if we have conversation detail */}
                {currentConversationDetail && currentConversationDetail.responses.length > 0 ? (
                  <>
                    <Box sx={{ mb: 3 }}>
                      <ResponseTimeline
                        responses={currentConversationDetail.responses}
                        showAllItems={showAllItems}
                        onToggleShowAll={() => setShowAllItems(!showAllItems)}
                        onCopyMessage={handleCopyMessage}
                        onGoodResponse={handleGoodResponse}
                        onBadResponse={handleBadResponse}
                      />
                    </Box>
                    {/* Show ThinkingMessage if waiting for response or latest response is still processing */}
                    {(() => {
                      const latestResponse = currentConversationDetail.responses[currentConversationDetail.responses.length - 1];
                      const isLatestResponseProcessing = latestResponse?.status === 'queued' || latestResponse?.status === 'in_progress';
                      if (isWaitingForResponse || isLatestResponseProcessing) {
                        return (
                          <Box sx={{ mb: 2 }}>
                            <ThinkingMessage />
                          </Box>
                        );
                      }
                      return null;
                    })()}
                  </>
                ) : (
                  <Box sx={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
                    {isWaitingForResponse ? (
                      <ThinkingMessage />
                    ) : (
                      <>
                        <Typography variant="h5" sx={{ color: 'text.secondary', mb: 1 }}>
                          What can I help with?
                        </Typography>
                        <Typography variant="body1" sx={{ color: 'text.secondary', mb: 4 }}>
                          I have access to the metadata for the organization and can answer questions about it.
                        </Typography>
                        <Box sx={{ width: '100%', maxWidth: 600 }}>
                          <MessageInput
                            value={inputValue}
                            onChange={setInputValue}
                            onSubmit={handleSubmit}
                            disabled={isWaitingForResponse || isLoadingConversation || !isWorkspaceReady}
                            placeholder={isWorkspaceReady ? "Type your message here..." : "Workspace is not ready..."}
                            containerAppUrl={containerAppUrl}
                          />
                        </Box>
                      </>
                    )}
                  </Box>
                )}
              </Box>
            </Box>

            {/* Input Area - Fixed at bottom (only when conversation is selected) */}
            {currentConversationDetail && currentConversationDetail.responses.length > 0 && (
              <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
                  <Box sx={{ width: '100%', maxWidth: 800 }}>
                  <MessageInput
                    value={inputValue}
                    onChange={setInputValue}
                    onSubmit={handleSubmit}
                    disabled={isWaitingForResponse || isLoadingConversation || !isWorkspaceReady}
                    placeholder={isWorkspaceReady ? "Type your message here..." : "Workspace is not ready..."}
                    containerAppUrl={containerAppUrl}
                  />
                </Box>
              </Box>
            )}
          </>
        )}

        {currentView === 'analysis' && activeWorkspace?.snapshot_id && activeWorkspaceId && (
          <Box sx={{ flex: 1, overflow: 'hidden', display: 'flex', flexDirection: 'column', minHeight: 0 }}>
            <Analysis
              snapshotId={activeWorkspace.snapshot_id}
              workspaceId={activeWorkspaceId}
            />
          </Box>
        )}

        {currentView === 'insights' && activeWorkspace?.snapshot_id && activeWorkspaceId && (
          <Box sx={{ flex: 1, overflow: 'auto' }}>
            <Insights key={insightsNavKey} snapshotId={activeWorkspace.snapshot_id} workspaceId={activeWorkspaceId} />
          </Box>
        )}

        {currentView === 'metadata' && orgId && activeWorkspaceId && containerAppUrl && (
          <Box sx={{ flex: 1, overflow: 'hidden' }}>
            <MetadataBrowser
              orgId={orgId}
							workspaceId={activeWorkspaceId}
              containerAppUrl={containerAppUrl}
              selectedPath={selectedMetadataPath}
              onPathSelect={setSelectedMetadataPath}
              currentUserId={user?.user_id ?? null}
            />
          </Box>
        )}

        {currentView === 'documents' && containerAppUrl && user?.user_id && (
          <Box sx={{ flex: 1, overflow: 'hidden' }}>
            <OutputDocs
              containerAppUrl={containerAppUrl}
              userId={user.user_id}
            />
          </Box>
        )}

         {/* AI Disclaimer Footer - Always at bottom */}
         <Box sx={{ display: 'flex', justifyContent: 'center', pb: 2 }}>
           <Box sx={{ width: '100%', maxWidth: 800, px: 2 }}>
             <Typography 
               variant="caption" 
               sx={{ 
                 display: 'block',
                 textAlign: 'center',
                 color: 'text.secondary',
                 px: 1,
                 fontSize: '0.75rem',
                 lineHeight: 1.2
               }}
             >
               Generated by AI. May contain inaccuracies or biases. Please review responses critically and consult additional expertise as needed.
             </Typography>
           </Box>
         </Box>
      </Box>

      {/* Success Alert Snackbar */}
      <Snackbar
        open={alertOpen}
        autoHideDuration={2000}
        onClose={handleCloseAlert}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={handleCloseAlert} severity="success" sx={{ width: '100%' }}>
          {alertMessage}
        </Alert>
      </Snackbar>

      {/* Disclaimer Modal */}
      <DisclaimerModal
        open={showBetaModal}
        onClose={handleCloseBetaModal}
      />

      {/* Workspace Manager Modal */}
      <Dialog
        open={showWorkspaceManager}
        onClose={handleWorkspaceManagerClose}
        maxWidth="md"
        fullWidth
        disableEscapeKeyDown={workspaces.length === 0}
        PaperProps={{
          sx: {
            borderRadius: 2,
          }
        }}
      >
        <DialogTitle>Org Overviews</DialogTitle>
        <DialogContent>
          <WorkspaceManager
            activeWorkspaceId={activeWorkspaceId}
            onWorkspaceSelect={handleWorkspaceSelect}
            onWorkspaceAdded={handleWorkspaceAdded}
          />
        </DialogContent>
      </Dialog>

      {/* Beta Menu */}
      <Menu
        anchorEl={betaMenuAnchor}
        open={Boolean(betaMenuAnchor)}
        onClose={handleBetaMenuClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
      >
        <MenuItem onClick={handleBetaDetails}>
          <WarningIcon sx={{ mr: 2, fontSize: '1.2rem' }} />
          PoC Details
        </MenuItem>
        <MenuItem onClick={handleReleaseNotes}>
          <DescriptionIcon sx={{ mr: 2, fontSize: '1.2rem' }} />
          Release Notes
        </MenuItem>
        <MenuItem onClick={handleLeaveFeedback}>
          <FeedbackIcon sx={{ mr: 2, fontSize: '1.2rem' }} />
          Send Feedback
        </MenuItem>
      </Menu>

      {/* Org Menu */}
      <OrgMenu
        anchorEl={orgMenuAnchor}
        open={Boolean(orgMenuAnchor)}
        onClose={handleOrgMenuClose}
        onSwitchOrg={handleSwitchOrg}
        onRefreshMetadata={handleRefreshMetadata}
				orgName={orgInfo.name}
				orgUsername={orgInfo.username}
				hasWarnings={!!activeWorkspace?.snapshot_apply_warnings}
				onViewWarnings={() => setWarningsDialogOpen(true)}
      />

		<UserMenu
			anchorEl={userMenuAnchor}
			open={Boolean(userMenuAnchor)}
			onClose={handleUserMenuClose}
			onLogout={handleUserLogout}
			displayName={userDisplayName}
			email={userEmail}
		/>

		{/* Snapshot warnings dialog */}
		{activeWorkspace?.snapshot_apply_warnings && (() => {
			let warnings: { skipped_count: number; skipped_files: string[] } | null = null;
			try { warnings = JSON.parse(activeWorkspace.snapshot_apply_warnings); } catch { /* ignore */ }
			if (!warnings || warnings.skipped_count === 0) return null;
			return (
				<Dialog
			open={warningsDialogOpen}
				onClose={handleWarningsDialogClose}
					maxWidth="sm"
					fullWidth
				>
					<DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
						<WarningAmberIcon sx={{ color: 'warning.main' }} />
						Metadata Snapshot Warnings
					</DialogTitle>
					<DialogContent>
						<Typography variant="body2" sx={{ mb: 2 }}>
							{warnings.skipped_count} metadata file{warnings.skipped_count !== 1 ? 's were' : ' was'} skipped
							during the last workspace sync. These files have names containing characters
							(<Box component="code" sx={{ fontFamily: 'monospace', fontSize: '0.8em' }}>{'* : " < > ? |'}</Box>)
							that Azure File Share does not allow. Their content is intact in blob storage but not available in the workspace filesystem.
						</Typography>
						<Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
							This is a known infrastructure limitation. A permanent fix is planned.
							Refreshing metadata will not resolve this until the infrastructure is upgraded.
						</Typography>
						<Chip
							label={`${warnings.skipped_count} skipped file${warnings.skipped_count !== 1 ? 's' : ''}`}
							color="warning"
							size="small"
							sx={{ mb: 1 }}
						/>
						<List dense disablePadding sx={{ maxHeight: 240, overflowY: 'auto', border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
							{warnings.skipped_files.map((file) => (
								<ListItem key={file} sx={{ py: 0.25 }}>
									<Typography variant="caption" sx={{ fontFamily: 'monospace', wordBreak: 'break-all' }}>
										{file}
									</Typography>
								</ListItem>
							))}
						</List>
					</DialogContent>
					<DialogActions>
						<Button onClick={handleWarningsDialogClose}>Close</Button>
					</DialogActions>
				</Dialog>
			);
		})()}

      {/* Conversation actions menu */}
      <Menu
        anchorEl={convMenuAnchorEl}
        open={Boolean(convMenuAnchorEl)}
        onClose={handleConvMenuClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        disableRestoreFocus
      >
        <MenuItem onClick={handleRenameOpen}>Rename</MenuItem>
        <MenuItem onClick={handleArchive}>Archive</MenuItem>
        <MenuItem onClick={handleDeleteOpen} sx={{ color: 'error.main' }}>Delete</MenuItem>
      </Menu>

      {/* Delete confirmation dialog */}
      <Dialog
        open={Boolean(deleteConfirmConvId)}
        onClose={/* istanbul ignore next -- MUI Dialog onClose is triggered by backdrop click/Escape in the real browser; jsdom does not synthesise these Portal events */ () => setDeleteConfirmConvId(null)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Delete conversation?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            This will permanently delete this conversation and all its messages. This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteConfirmConvId(null)}>Cancel</Button>
          <Button onClick={handleDeleteConfirm} color="error" variant="contained">Delete</Button>
        </DialogActions>
      </Dialog>

      {/* Workspace Initialization Progress */}
      <WorkspaceInitializationProgress
        isVisible={progress.isVisible}
        percentage={progress.percentage}
        message={progress.message}
        title={progress.title}
      />

      {/* Loading Backdrop for Conversation Loading */}
      <Backdrop
        sx={{ 
          color: '#fff', 
          zIndex: (theme) => theme.zIndex.drawer + 1,
          backgroundColor: 'rgba(0, 0, 0, 0.5)'
        }}
        open={isLoadingConversation}
      >
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
          <CircularProgress color="inherit" />
          <Typography variant="h6">Loading conversation...</Typography>
        </Box>
      </Backdrop>
			</Box>
		</>
	);
};

export default AppV2;
